import React, { useState } from 'react';
import { Label } from '@atlaskit/form';

import ErrorIcon from '@atlaskit/icon/core/migration/error';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { Radio } from '@atlaskit/radio';
import Select from '@atlaskit/select';

import { token } from '@atlaskit/tokens';
import AtlaskitLink from '@atlaskit/link';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { FormattedMessage, useIntl, type MessageDescriptorV2 } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	RequestTypeIssueSecurityState,
	SecurityLevelOption,
} from '../../../../controllers/issue-security/types.tsx';
import {
	isDefaultNotAllowed,
	isMisconfiguredMissingLevel,
	isMissingValueForRequiredField,
	isMissingValueForSelectOption,
} from '../../../../controllers/issue-security/utils.tsx';
import { DOCUMENTATION_LINK } from './constants.tsx';
import { ConfigEmptyState } from './empty-state/index.tsx';
import messages from './messages.tsx';
import { SelectOption, SelectOptionStandalone } from './select-option/index.tsx';
import { SelectSingleValue } from './select-single-value/index.tsx';
import type { Props, SecurityOption, SecurityOptionGroup } from './types.tsx';

export const IssueSecurityConfig = ({ projectKey, issueSecurityState, makeSelection }: Props) => {
	const { formatMessage } = useIntl();
	const {
		selectedSecurityLevelId,
		defaultSecurityLevelId,
		securityLevels,
		isRequired,
		securityLevelOption,
		showError,
	} = issueSecurityState;

	const securityLevelOptions: SecurityOption[] = securityLevels.map((level) => ({
		value: level.id,
		label: level.name,
		isDefault: level.id === defaultSecurityLevelId,
		isMember: level.isMember,
		description: level.description,
	}));
	const securityLevelGroupedOptions: SecurityOptionGroup[] = [
		{
			label: formatMessage(messages.memberOfSecurityLevel),
			options: securityLevelOptions.filter(({ isMember }) => isMember),
		},
		{
			label: formatMessage(messages.notMemberOfSecurityLevel),
			options: securityLevelOptions.filter(({ isMember }) => !isMember),
		},
	];
	const defaultSecurityLevel = securityLevelOptions.find(
		(level) => level.value === defaultSecurityLevelId,
	);

	const misconfiguredMissingLevel = showError && isMisconfiguredMissingLevel(issueSecurityState);
	const misconfiguredRequired =
		showError &&
		(isMissingValueForRequiredField(issueSecurityState) ||
			isMissingValueForSelectOption(issueSecurityState));

	const [defaultSecurityLevelRadioPopupOpen, setDefaultSecurityLevelRadioPopupOpen] =
		useState(false);

	const manageSecurityLink = (
		<LinkDocumentation
			projectKey={projectKey}
			issueSecurityState={issueSecurityState}
			message={messages.manageSecurityLevelsLink}
		/>
	);

	const onRadioChange = (option: SecurityLevelOption) => {
		if (isDefaultNotAllowed(issueSecurityState) && option === 'DEFAULT') {
			setDefaultSecurityLevelRadioPopupOpen(true);
		} else {
			makeSelection(option, selectedSecurityLevelId);
		}
	};

	const securityLevelOptionDisplay = () => {
		if (securityLevelOption === 'SELECT' || !defaultSecurityLevel) {
			return (
				<Stack space="space.050">
					<Inline space="space.025">
						<Label htmlFor="select-security-level">{formatMessage(messages.selectLabel)}</Label>
						{isRequired || defaultSecurityLevel ? <Text color="color.text.danger">*</Text> : null}
					</Inline>
					<Select<SecurityOption>
						classNamePrefix="issue-security-picker"
						styles={{
							menuPortal: (base) => ({
								...base,
								zIndex: layers.modal + 1,
							}),
						}}
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						menuPortalTarget={document.body}
						isClearable={!isRequired && !defaultSecurityLevel}
						menuPlacement="auto"
						options={securityLevelGroupedOptions}
						components={{
							Option: SelectOption,
							SingleValue: SelectSingleValue,
						}}
						onChange={(newValue) => makeSelection('SELECT', newValue ? newValue.value : undefined)}
						label={formatMessage(messages.selectLabel)}
						placeholder={formatMessage(messages.placeholder)}
						value={securityLevelOptions.find((level) => level.value === selectedSecurityLevelId)}
						isInvalid={misconfiguredMissingLevel || misconfiguredRequired}
					/>
					{misconfiguredMissingLevel && (
						<ErrorMessage message={formatMessage(messages.missingLevelError)} />
					)}
					{misconfiguredRequired && (
						<ErrorMessage message={formatMessage(messages.requiredError)} />
					)}
				</Stack>
			);
		}

		return (
			<Stack space="space.050">
				<Label htmlFor="default-security-level">{formatMessage(messages.selectLabel)}</Label>
				<SelectOptionStandalone
					label={defaultSecurityLevel.label}
					description={defaultSecurityLevel.description}
				/>
			</Stack>
		);
	};

	return (
		<>
			<FireUiAnalytics
				actionSubject="tab"
				action="viewed"
				actionSubjectId="requestTypeIssueSecurity"
				attributes={getAnalyticsAttributes(issueSecurityState)}
			/>
			{securityLevels.length > 0 ? (
				<Stack space="space.300">
					<Box>
						<FormattedMessage
							{...(fg('itsm-issue-renaming')
								? messages.descriptionIssueTermRefresh
								: messages.description)}
							values={{
								link: (chunks) =>
									fg('jsm_request_intake_enghealth_due_in_feb') ? (
										<AtlaskitLink href={DOCUMENTATION_LINK} target="_blank" appearance="default">
											{chunks}
										</AtlaskitLink>
									) : (
										<Link href={DOCUMENTATION_LINK} target="_blank">
											{chunks}
										</Link>
									),
							}}
						/>
					</Box>
					<Stack space="space.200">
						<Stack space="space.050">
							<Popup
								content={() => (
									<Stack xcss={popupContentStyles}>
										{isRequired ? (
											<FormattedMessage {...messages.isRequiredPopup} />
										) : (
											<>
												<FormattedMessage
													{...(fg('itsm-issue-renaming')
														? messages.noDefaultSecurityLevelPopupIssueTermRefresh
														: messages.noDefaultSecurityLevelPopup)}
												/>
												{manageSecurityLink}
											</>
										)}
									</Stack>
								)}
								zIndex={layers.modal + 1}
								placement="bottom-start"
								messageId="servicedesk-request-type-security.ui.config-button.config-modal.issue-security-config.popup"
								messageType="transactional"
								isOpen={defaultSecurityLevelRadioPopupOpen}
								onClose={() => setDefaultSecurityLevelRadioPopupOpen(false)}
								trigger={(triggerProps) => (
									<Inline>
										<Radio
											{...triggerProps}
											aria-expanded={undefined}
											isChecked={securityLevelOption === 'DEFAULT'}
											label={formatMessage(messages.alwaysUseProjectDefaultSecurityLevel)}
											onClick={() => onRadioChange('DEFAULT')}
										/>
									</Inline>
								)}
							/>
							<Inline>
								<Radio
									isChecked={securityLevelOption === 'SELECT'}
									label={formatMessage(messages.selectSecurityLevel)}
									onClick={() => onRadioChange('SELECT')}
								/>
							</Inline>
						</Stack>
						<Stack space="space.200">
							{securityLevelOptionDisplay()}
							{manageSecurityLink}
						</Stack>
					</Stack>
				</Stack>
			) : (
				<ConfigEmptyState
					link={
						<LinkDocumentation
							projectKey={projectKey}
							issueSecurityState={issueSecurityState}
							message={
								fg('itsm-issue-renaming')
									? messages.goToIssueSecurityLinkIssueTermRefresh
									: messages.goToIssueSecurityLink
							}
						/>
					}
				/>
			)}
		</>
	);
};

const ErrorMessage = ({ message }: { message: string }) => (
	<Inline
		space="space.050"
		testId="servicedesk-request-type-security.ui.config-button.config-modal.issue-security-config.misconfiguration-error-message"
	>
		<ErrorIcon LEGACY_size="small" label={message} color={token('color.icon.danger')} />
		<Text size="small" color="color.text.danger">
			{message}
		</Text>
	</Inline>
);

const LinkDocumentation = ({
	projectKey,
	issueSecurityState,
	message,
}: {
	projectKey: string;
	issueSecurityState: RequestTypeIssueSecurityState;
	message: MessageDescriptorV2;
}) =>
	fg('jsm_request_intake_enghealth_due_in_feb') ? (
		<AtlaskitLink
			href={`/plugins/servlet/project-config/${projectKey}/issuesecurity`}
			target="_blank"
			onClick={(_: unknown, event: UIAnalyticsEvent) =>
				fireUIAnalytics(
					event,
					'manageIssueSecurityLink',
					getAnalyticsAttributes(issueSecurityState),
				)
			}
		>
			<FormattedMessage {...message} />
		</AtlaskitLink>
	) : (
		<Link
			href={`/plugins/servlet/project-config/${projectKey}/issuesecurity`}
			target="_blank"
			onClick={(_: unknown, event: UIAnalyticsEvent) =>
				fireUIAnalytics(
					event,
					'manageIssueSecurityLink',
					getAnalyticsAttributes(issueSecurityState),
				)
			}
		>
			<Inline alignBlock="center" space="space.050">
				<FormattedMessage {...message} />
				<ShortcutIcon LEGACY_size="small" label="" />
			</Inline>
		</Link>
	);

const getAnalyticsAttributes = (issueSecurityState: RequestTypeIssueSecurityState) => ({
	hasDefaultSecurityLevel: !!issueSecurityState.defaultSecurityLevelId,
	selectedSecurityLevelId: issueSecurityState.selectedSecurityLevelId,
	defaultSecurityLevelId: issueSecurityState.defaultSecurityLevelId,
	issueSecurityLevelsCount: issueSecurityState.securityLevels.length,
});

const popupContentStyles = xcss({
	maxWidth: '14rem',
	padding: 'space.200',
});
